/**
 * Shared content/config for the supporter role dashboards.
 * Everything here is NON-FINANCIAL: awareness / education / community only.
 * Role-specific nav sections + educational content live inside each dashboard
 * component for cohesion; this file holds cross-role collaboration options and
 * shared disclaimers.
 */
import type { CollaborationInterestOption } from '@/components/supporter/SupporterCollaborationForm'
import type { SupporterRole } from '@/components/supporter/SupporterProfilePanel'

// ──────────────────────────────────────────────────────────────────────────
// Collaboration "Express Interest" options per role.
// Values are free-form strings stored on the backend (SupporterCollaborationInterest.interestType).
// ──────────────────────────────────────────────────────────────────────────
export const COLLABORATION_OPTIONS: Record<SupporterRole, CollaborationInterestOption[]> = {
  nutrition: [
    { value: 'NUTRITION_AWARENESS_CAMPAIGN', label: 'Nutrition awareness campaign' },
    { value: 'EDUCATIONAL_PROGRAMME', label: 'Educational programme' },
    { value: 'COMMUNITY_INITIATIVE', label: 'Community initiative' },
    { value: 'CONTENT_COLLABORATION', label: 'Content collaboration' },
    { value: 'VOLUNTEER_NUTRITION_ACTIVITY', label: 'Volunteer nutrition-awareness activity' },
  ],
  corporate: [
    { value: 'AWARENESS_CAMPAIGN', label: 'Awareness campaign' },
    { value: 'EMPLOYEE_EDUCATION', label: 'Employee education' },
    { value: 'COMMUNITY_CAMPAIGN', label: 'Community campaign' },
    { value: 'VOLUNTEER_INITIATIVE', label: 'Volunteer initiative' },
    { value: 'KNOWLEDGE_PARTNERSHIP', label: 'Knowledge partnership' },
    { value: 'WORKPLACE_AWARENESS_PROGRAMME', label: 'Workplace awareness programme' },
  ],
  ngo: [
    { value: 'GOVERNMENT_INITIATIVE', label: 'Government awareness initiative' },
    { value: 'COMMUNITY_CAMPAIGN', label: 'Community campaign' },
    { value: 'VOLUNTEER_NETWORK', label: 'Volunteer network' },
    { value: 'EDUCATIONAL_INITIATIVE', label: 'Educational initiative' },
    { value: 'TB_AWARENESS_PROGRAMME', label: 'TB awareness programme' },
    { value: 'RESEARCH_KNOWLEDGE_INITIATIVE', label: 'Research / knowledge initiative' },
  ],
  individual: [
    { value: 'AWARENESS_CAMPAIGN', label: 'Awareness campaign participation' },
    { value: 'COMMUNITY_EVENT', label: 'Community event' },
    { value: 'EDUCATIONAL_OUTREACH', label: 'Educational outreach' },
    { value: 'CONTENT_SHARING', label: 'Content sharing / digital awareness' },
    { value: 'STUDENT_COMMUNITY_ACTIVITY', label: 'Student / community activity' },
  ],
}

// ──────────────────────────────────────────────────────────────────────────
// Shared disclaimers (non-diagnostic, non-financial).
// ──────────────────────────────────────────────────────────────────────────
export const MEDICAL_DISCLAIMER =
  'This is awareness information only — not a diagnosis or medical advice. ' +
  'Always consult a qualified healthcare professional for medical decisions.'

export const NUTRITION_DISCLAIMER =
  'Educational nutrition awareness only — not personalised medical or dietary ' +
  'prescriptions. Consult a qualified professional for individual nutrition advice.'

export const IMPACT_DISCLAIMER =
  'Platform-reported activity indicators only. These reflect awareness activity, ' +
  'not medical outcomes and not any monetary value.'

// ──────────────────────────────────────────────────────────────────────────
// Campaign `type` filters per role (AWARENESS / EDUCATION / COMMUNITY_OUTREACH …).
// Used to surface relevant active campaigns on each dashboard.
// ──────────────────────────────────────────────────────────────────────────
export const AWARENESS_CAMPAIGN_TYPES = ['AWARENESS', 'EDUCATION', 'COMMUNITY_OUTREACH']
export const NUTRITION_CAMPAIGN_TYPES = ['AWARENESS', 'EDUCATION', 'COMMUNITY_OUTREACH', 'SUPPORT']